import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Plus, 
  Search,
  Shield,
  Mail,
  MoreVertical,
  Pencil,
  Trash2,
  UserCog,
  CheckCircle,
  XCircle,
  LayoutDashboard,
  FileText,
  ShoppingCart,
  FolderKanban,
  DollarSign,
  Package,
  Settings,
  BarChart,
  Monitor,
  ShoppingBag,
  Check,
  Key,
  Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Module icons mapping
const moduleIcons = {
  dashboard: LayoutDashboard,
  tenders: FileText,
  crm: Users,
  sales: ShoppingCart,
  projects: FolderKanban,
  finance: DollarSign,
  procurement: ShoppingBag,
  inventory: Package,
  hr: UserCog,
  pos: Monitor,
  reports: BarChart,
  settings: Settings,
};

const roleColors = {
  admin: 'bg-red-100 text-red-700 border-red-200',
  manager: 'bg-purple-100 text-purple-700 border-purple-200',
  sales: 'bg-blue-100 text-blue-700 border-blue-200',
  support: 'bg-amber-100 text-amber-700 border-amber-200',
  user: 'bg-gray-100 text-gray-700 border-gray-200'
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showModulesDialog, setShowModulesDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showCompanyAccessDialog, setShowCompanyAccessDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ module_access: [], company_access: [] });
  const [newPassword, setNewPassword] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchModules();
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/companies`);
      setCompanies(res.data.companies || []);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users`);
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/roles`);
      setRoles(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/modules`);
      setModules(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    if (!formData.email || !formData.full_name || !formData.password) {
      toast.error('Please fill all required fields');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/admin/users`, formData);
      toast.success('User created successfully');
      setShowDialog(false);
      setFormData({ module_access: [] });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateModules = async () => {
    if (!editingUser) return;
    try {
      await axios.put(`${API_URL}/api/admin/users/${editingUser.id}/modules`, formData.module_access);
      toast.success('Module access updated');
      setShowModulesDialog(false);
      setEditingUser(null);
      setFormData({ module_access: [] });
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update modules');
    }
  };

  const toggleModule = (moduleId) => {
    const current = formData.module_access || [];
    if (current.includes(moduleId)) {
      setFormData({ ...formData, module_access: current.filter(m => m !== moduleId) });
    } else {
      setFormData({ ...formData, module_access: [...current, moduleId] });
    }
  };

  const selectAllModules = () => {
    setFormData({ ...formData, module_access: modules.map(m => m.id) });
  };

  const clearAllModules = () => {
    setFormData({ ...formData, module_access: [] });
  };

  const openEditModules = (user) => {
    setEditingUser(user);
    setFormData({ module_access: user.module_access || [] });
    setShowModulesDialog(true);
  };

  const openEditCompanyAccess = (user) => {
    setEditingUser(user);
    setFormData({ company_access: user.company_access || [] });
    setShowCompanyAccessDialog(true);
  };

  const toggleCompanyAccess = (companyId, companyName) => {
    const current = formData.company_access || [];
    const exists = current.find(c => c.company_id === companyId);
    if (exists) {
      setFormData({ ...formData, company_access: current.filter(c => c.company_id !== companyId) });
    } else {
      setFormData({ ...formData, company_access: [...current, { company_id: companyId, company_name: companyName, role: 'member' }] });
    }
  };

  const updateCompanyRole = (companyId, role) => {
    const current = formData.company_access || [];
    setFormData({
      ...formData,
      company_access: current.map(c => c.company_id === companyId ? { ...c, role } : c)
    });
  };

  const handleUpdateCompanyAccess = async () => {
    if (!editingUser) return;
    try {
      await axios.put(`${API_URL}/api/admin/users/${editingUser.id}`, { company_access: formData.company_access });
      toast.success('Company access updated');
      setShowCompanyAccessDialog(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update company access');
    }
  };

  const openChangePassword = (user) => {
    setEditingUser(user);
    setNewPassword('');
    setShowPasswordDialog(true);
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.put(`${API_URL}/api/admin/users/${editingUser.id}/password`, { new_password: newPassword });
      toast.success('Password changed successfully');
      setShowPasswordDialog(false);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    }
  };

  const handleDelete = async (userId) => {
    if (!userId) {
      toast.error('Cannot delete user: Invalid user ID');
      return;
    }
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await axios.delete(`${API_URL}/api/admin/users/${userId}`);
        toast.success('User deleted');
        fetchUsers();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to delete user');
      }
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/role?role=${newRole}`);
      toast.success('Role updated');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update role');
    }
  };

  const handleToggleActive = async (userId, isActive) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}`, { is_active: !isActive });
      toast.success(isActive ? 'User deactivated' : 'User activated');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update user');
    }
  };

  const openNew = () => {
    setFormData({ role: 'user', module_access: ['dashboard'] });
    setShowDialog(true);
  };

  // Group modules by category for better UI
  const groupedModules = modules.reduce((acc, module) => {
    const group = module.group || 'Other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(module);
    return acc;
  }, {});

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: users.length,
    active: users.filter(u => u.is_active !== false).length,
    admins: users.filter(u => u.role === 'admin').length
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">User Management</h1>
          <p className="text-muted-foreground">Manage users and their roles.</p>
        </div>
        <Button onClick={openNew} className="gap-2"><Plus className="h-4 w-4" /> Add User</Button>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.total}</div><div className="text-sm text-muted-foreground">Total Users</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-emerald-600">{stats.active}</div><div className="text-sm text-muted-foreground">Active</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-red-600">{stats.admins}</div><div className="text-sm text-muted-foreground">Admins</div></CardContent></Card>
      </div>

      {/* Roles Overview */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5" /> Available Roles</CardTitle></CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-5 gap-4">
            {roles.map(role => (
              <div key={role.id} className="border rounded-lg p-4">
                <Badge className={roleColors[role.id]}>{role.name}</Badge>
                <p className="text-xs text-muted-foreground mt-2">{role.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input placeholder="Search users..." className="pl-10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>User</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Module Access</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="w-[80px] text-center">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold">
                      {user.full_name?.[0] || user.email?.[0]?.toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium">{user.full_name || 'No Name'}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground">{user.email}</TableCell>
                <TableCell>
                  <Select value={user.role || 'user'} onValueChange={(v) => handleRoleChange(user.id, v)}>
                    <SelectTrigger className="w-32 h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map(role => (
                        <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  {user.role === 'admin' ? (
                    <Badge className="bg-red-100 text-red-700">All Modules</Badge>
                  ) : (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-7 text-xs"
                      onClick={() => openEditModules(user)}
                    >
                      <Shield className="h-3 w-3 mr-1" />
                      {(user.module_access || []).length} of {modules.length}
                    </Button>
                  )}
                </TableCell>
                <TableCell>
                  {user.is_active !== false ? (
                    <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-700">Inactive</Badge>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : '-'}
                </TableCell>
                <TableCell className="text-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="h-8 px-2">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => openEditModules(user)}>
                        <Shield className="h-4 w-4 mr-2" /> Manage Modules
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEditCompanyAccess(user)}>
                        <Building2 className="h-4 w-4 mr-2" /> Company Access
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openChangePassword(user)}>
                        <Key className="h-4 w-4 mr-2" /> Change Password
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleToggleActive(user.id, user.is_active !== false)}>
                        {user.is_active !== false ? (
                          <><XCircle className="h-4 w-4 mr-2" /> Deactivate</>
                        ) : (
                          <><CheckCircle className="h-4 w-4 mr-2" /> Activate</>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => handleDelete(user.id)}>
                        <Trash2 className="h-4 w-4 mr-2" /> Delete User
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filteredUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="h-32 text-center">
                  <Users className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">No users found</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add User Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader><DialogTitle>Add New User</DialogTitle></DialogHeader>
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="basic">Basic Info</TabsTrigger>
              <TabsTrigger value="companies">Companies</TabsTrigger>
              <TabsTrigger value="modules">Modules</TabsTrigger>
            </TabsList>
            <TabsContent value="basic" className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input value={formData.full_name || ''} onChange={e => setFormData({...formData, full_name: e.target.value})} placeholder="John Doe" />
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input type="email" value={formData.email || ''} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="john@company.com" />
              </div>
              <div className="space-y-2">
                <Label>Password *</Label>
                <Input type="password" value={formData.password || ''} onChange={e => setFormData({...formData, password: e.target.value})} placeholder="••••••••" />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={formData.role || 'user'} onValueChange={v => {
                  if (v === 'admin') {
                    setFormData({...formData, role: v, module_access: modules.map(m => m.id)});
                  } else {
                    setFormData({...formData, role: v});
                  }
                }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {roles.map(role => (
                      <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.role === 'admin' && (
                  <p className="text-xs text-amber-600">Admin role grants access to all modules automatically.</p>
                )}
              </div>
            </TabsContent>
            <TabsContent value="companies" className="py-4">
              <p className="text-sm text-muted-foreground mb-4">Select which companies this user can access</p>
              <ScrollArea className="h-[250px] pr-4">
                <div className="space-y-2">
                  {companies.map(company => {
                    const access = (formData.company_access || []).find(c => c.company_id === company.id);
                    return (
                      <div 
                        key={company.id} 
                        className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                          access ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox
                            checked={!!access}
                            onCheckedChange={() => toggleCompanyAccess(company.id, company.name)}
                          />
                          <div>
                            <p className="text-sm font-medium">{company.name}</p>
                            <p className="text-xs text-muted-foreground">{company.city}, {company.country}</p>
                          </div>
                        </div>
                        {access && (
                          <Select
                            value={access.role}
                            onValueChange={(value) => updateCompanyRole(company.id, value)}
                          >
                            <SelectTrigger className="w-28 h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="manager">Manager</SelectItem>
                              <SelectItem value="member">Member</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  })}
                  {companies.length === 0 && (
                    <p className="text-center text-muted-foreground py-4">No companies available. Create a company first.</p>
                  )}
                </div>
              </ScrollArea>
            </TabsContent>
            <TabsContent value="modules" className="py-4">
              <div className="flex items-center justify-between mb-4">
                <div className="text-sm text-muted-foreground">
                  Selected: {(formData.module_access || []).length} of {modules.length} modules
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={selectAllModules}>Select All</Button>
                  <Button variant="outline" size="sm" onClick={clearAllModules}>Clear All</Button>
                </div>
              </div>
              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-4">
                  {Object.entries(groupedModules).map(([group, groupModules]) => (
                    <div key={group}>
                      <h4 className="text-sm font-semibold text-muted-foreground mb-2">{group}</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {groupModules.map(module => {
                          const IconComponent = moduleIcons[module.id] || Shield;
                          const isSelected = (formData.module_access || []).includes(module.id);
                          return (
                            <div
                              key={module.id}
                              onClick={() => toggleModule(module.id)}
                              className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                isSelected 
                                  ? 'border-primary bg-primary/5' 
                                  : 'border-border hover:border-primary/50'
                              }`}
                            >
                              <div className={`p-2 rounded-md ${isSelected ? 'bg-primary/10' : 'bg-muted'}`}>
                                <IconComponent className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-sm">{module.name}</div>
                                {module.submodules?.length > 0 && (
                                  <div className="text-xs text-muted-foreground">
                                    {module.submodules.length} sub-modules
                                  </div>
                                )}
                              </div>
                              {isSelected && <Check className="h-4 w-4 text-primary" />}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave}>Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Module Access Dialog */}
      <Dialog open={showModulesDialog} onOpenChange={setShowModulesDialog}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle>Manage Module Access</DialogTitle>
            {editingUser && (
              <p className="text-sm text-muted-foreground">
                Configure which modules <span className="font-medium">{editingUser.full_name}</span> can access.
              </p>
            )}
          </DialogHeader>
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-muted-foreground">
              Selected: {(formData.module_access || []).length} of {modules.length} modules
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAllModules}>Select All</Button>
              <Button variant="outline" size="sm" onClick={clearAllModules}>Clear All</Button>
            </div>
          </div>
          <ScrollArea className="h-[400px] pr-4">
            <div className="space-y-4">
              {Object.entries(groupedModules).map(([group, groupModules]) => (
                <div key={group}>
                  <h4 className="text-sm font-semibold text-muted-foreground mb-2">{group}</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {groupModules.map(module => {
                      const IconComponent = moduleIcons[module.id] || Shield;
                      const isSelected = (formData.module_access || []).includes(module.id);
                      return (
                        <div
                          key={module.id}
                          onClick={() => toggleModule(module.id)}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                            isSelected 
                              ? 'border-primary bg-primary/5' 
                              : 'border-border hover:border-primary/50'
                          }`}
                        >
                          <div className={`p-2 rounded-md ${isSelected ? 'bg-primary/10' : 'bg-muted'}`}>
                            <IconComponent className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                          </div>
                          <div className="flex-1">
                            <div className="font-medium text-sm">{module.name}</div>
                            {module.submodules?.length > 0 && (
                              <div className="text-xs text-muted-foreground">
                                Includes: {module.submodules.join(', ')}
                              </div>
                            )}
                          </div>
                          {isSelected && <Check className="h-4 w-4 text-primary" />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowModulesDialog(false); setEditingUser(null); }}>Cancel</Button>
            <Button onClick={handleUpdateModules}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Change Password
            </DialogTitle>
            {editingUser && (
              <p className="text-sm text-muted-foreground">
                Set a new password for <span className="font-medium">{editingUser.full_name}</span> ({editingUser.email})
              </p>
            )}
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input 
                id="new-password"
                type="password" 
                value={newPassword} 
                onChange={e => setNewPassword(e.target.value)} 
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              The user will need to use this new password on their next login.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowPasswordDialog(false); setEditingUser(null); setNewPassword(''); }}>Cancel</Button>
            <Button onClick={handleChangePassword}>Change Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Company Access Dialog */}
      <Dialog open={showCompanyAccessDialog} onOpenChange={setShowCompanyAccessDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Manage Company Access
            </DialogTitle>
            {editingUser && (
              <p className="text-sm text-muted-foreground">
                Control which companies <span className="font-medium">{editingUser.full_name}</span> can access
              </p>
            )}
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {companies.map(company => {
                const access = (formData.company_access || []).find(c => c.company_id === company.id);
                return (
                  <div 
                    key={company.id} 
                    className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                      access ? 'bg-primary/5 border-primary/30' : 'hover:bg-muted'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Checkbox
                        checked={!!access}
                        onCheckedChange={() => toggleCompanyAccess(company.id, company.name)}
                      />
                      <div>
                        <p className="text-sm font-medium">{company.name}</p>
                        <p className="text-xs text-muted-foreground">{company.city}, {company.country}</p>
                      </div>
                    </div>
                    {access && (
                      <Select
                        value={access.role}
                        onValueChange={(value) => updateCompanyRole(company.id, value)}
                      >
                        <SelectTrigger className="w-28 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="member">Member</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                );
              })}
              {companies.length === 0 && (
                <p className="text-center text-muted-foreground py-4">No companies available</p>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Users can only see and work with data from companies they have access to.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCompanyAccessDialog(false); setEditingUser(null); }}>Cancel</Button>
            <Button onClick={handleUpdateCompanyAccess}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;